/**
 * Volume of Interest Lookup Table Function
 *
 * @typedef {Function} VOILUTFunction
 * @param {number} modalityLutValue
 * @returns {number} transformed value
 * @memberof Objects
 */

/**
 *
 * @param {number} windowWidth Window Width
 * @param {number} windowCenter Window Center
 * @returns {VOILUTFunction} VOI LUT mapping function
 * @memberof VOILUT
 */
function generateLinearVOILUT(windowWidth, windowCenter) {
  return (modalityLutValue) => {
    return ((modalityLutValue - windowCenter) / windowWidth + 0.5) * 255;
  };
}

/**
 * Generate a non-linear volume of interest lookup table
 *
 * @param {LUT} voiLUT Volume of Interest Lookup Table Object
 * @returns {VOILUTFunction} VOI LUT mapping function
 * @memberof VOILUT
 */
function generateNonLinearVOILUT(voiLUT) {
  // We don't trust the voiLUT.numBitsPerEntry, mainly thanks to Agfa!
  const bitsPerEntry = Math.max(...voiLUT.lut).toString(2).length;
  const shift = bitsPerEntry - 8;
  const minValue = voiLUT.lut[0] >> shift;
  const maxValue = voiLUT.lut[voiLUT.lut.length - 1] >> shift;
  const maxValueMapped = voiLUT.firstValueMapped + voiLUT.lut.length - 1;

  return (modalityLutValue) => {
    if (modalityLutValue < voiLUT.firstValueMapped) {
      return minValue;
    }
    if (modalityLutValue >= maxValueMapped) {
      return maxValue;
    }

    return voiLUT.lut[modalityLutValue - voiLUT.firstValueMapped] >> shift;
  };
}

/**
 * Retrieve a VOI LUT mapping function given the current windowing settings
 * and the VOI LUT for the image
 *
 * @param {number} windowWidth Window Width
 * @param {number} windowCenter Window Center
 * @param {LUT} [voiLUT] Volume of Interest Lookup Table Object
 * @returns {VOILUTFunction} VOI LUT mapping function
 * @memberof VOILUT
 */
function getVOILut(windowWidth, windowCenter, voiLUT) {
  if (voiLUT) {
    return generateNonLinearVOILUT(voiLUT);
  }

  return generateLinearVOILUT(windowWidth, windowCenter);
}

export default getVOILut;
